/**
 * Wann der Schluss-Check vor der Vorschau steht — und welche Bilder er aufzählt.
 *
 * ⚠️ **Steht hier und nicht in der Komponente, damit ein Test sie lesen kann, ohne eine
 * Oberfläche zu rendern** — das Projekt hat kein jsdom. Dieselbe Begründung wie bei
 * `src/lib/fehlerliste.ts`.
 *
 * ⚠️ **Geldwirksam:** die Personenzahl trägt in V1 den Preis (`shared/personen.ts`). Die
 * tragende Zeile ist deshalb nicht „Check ja/nein", sondern der Vergleich mit der Summe,
 * für die zuletzt bestätigt wurde: wer danach eine Zahl ändert, muss wieder gefragt
 * werden. Ein einmal gesetztes Flag ließe genau den Fall durch, für den es den Check gibt.
 */

/** Was im Bereich unter der Kopfzeile steht. */
export type Ansicht = "reihe" | "pruefung" | "vorschau";

/** Das Wenige, das die Aufzählung von einem Bild braucht. */
export interface GezaehltesBild {
  bildId: string;
  personen: number;
  beschriftung: string;
}

/** Ein Bild, auf dem mehr als eines gezählt ist — mit seinem Platz in der Reihe. */
export interface Mehrfachbild extends GezaehltesBild {
  /** „Bild 3" — dieselbe Zählung wie am Auswahlring und in „Bild bearbeiten". */
  nummer: number;
}

/**
 * Wohin der Knopf „Ganze Reihe ansehen" führt.
 *
 * Aus der Vorschau und aus dem Check führt er **immer** zurück in die Reihe — er ist dort
 * mit „Weiter bearbeiten" beschriftet. Aus der Reihe führt er in den Check, solange die
 * Zählung für **diese** Summe nicht bestätigt ist; sonst gleich in die Vorschau.
 *
 * Ohne ein einziges Bild gibt es nichts zu zählen — dann wäre der Check eine Rückfrage
 * nach dem Nichts.
 */
export function naechsteAnsicht(
  jetzt: Ansicht,
  gezaehlt: { personen: number; bildelemente: number },
  geprueftBei: number | null,
): Ansicht {
  if (jetzt !== "reihe") return "reihe";
  if (gezaehlt.bildelemente === 0) return "vorschau";
  return geprueftBei === gezaehlt.personen ? "vorschau" : "pruefung";
}

/**
 * Die Bilder, auf denen mehr als eine Person oder ein Tier gezählt ist.
 *
 * Die Reihenfolge ist die der Reihe, und die Nummer ist der Platz darin — nicht der Index
 * in der gefilterten Liste. „Bild 2" muss dasselbe Bild meinen wie der Auswahlring.
 */
export function mehrfachbilder(bilder: readonly GezaehltesBild[]): Mehrfachbild[] {
  return bilder
    .map((bild, platz) => ({ ...bild, nummer: platz + 1 }))
    .filter((bild) => bild.personen > 1);
}
